import { trackUserBaseSelector } from '@fullstory/analytics';
import { trackEventArchived, trackEventSaved } from '@fullstory/analytics/events';
import { allSpecEventsBySlug, SpecObjectInfo } from '@fullstory/redux';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useProperties } from './useProperties';
import { useSelectedFsSpecObjectSlugs } from './useSelectedFsSpecObjectSlugs';

export const useAPIEventsShared = (
  name: string,
  onSave: (name: string, specSlugs: string[]) => void,
  onArchive: (name: string) => void,
  onUnarchive: (name: string) => void,
  initialSpecSlugs?: string[],
) => {
  const userAnalytics = useSelector(trackUserBaseSelector);
  const propertiesProps = useProperties(name);

  const archive = useCallback(() => {
    onArchive(name);
    trackEventArchived({
      ...userAnalytics,
      event_id_str: name,
      is_archived_bool: true,
    });
  }, [name, onArchive, userAnalytics]);

  const unArchive = useCallback(() => {
    onUnarchive(name);
    trackEventArchived({
      ...userAnalytics,
      event_id_str: name,
      is_archived_bool: false,
    });
  }, [name, onUnarchive, userAnalytics]);

  const disableArchive = !propertiesProps.canArchiveProperties;

  const specEventsBySlug = useSelector(allSpecEventsBySlug);
  const isExistingMapping = useCallback(
    (specObject: SpecObjectInfo): boolean =>
      !!specObject.mapping?.apiEventId && specObject.mapping.apiEventId === name,
    [name],
  );
  const {
    selectedFsSpecObjectSlugs,
    setSelectedFsSpecObjectSlugs,
    fsSpecEnabled,
    fsSpecRoleOptions,
    areRolesDirty,
    existingSpecObjects,
  } = useSelectedFsSpecObjectSlugs(specEventsBySlug, initialSpecSlugs || null, isExistingMapping);

  const save = useCallback(() => {
    const specSlugs = selectedFsSpecObjectSlugs?.map(option => option.value) || [];
    onSave(name, specSlugs);
    trackEventSaved({
      ...userAnalytics,
      event_id_str: name,
      spec_slugs: specSlugs,
    });
  }, [name, selectedFsSpecObjectSlugs, onSave, userAnalytics]);

  return {
    propertiesProps,
    save,
    archive,
    unArchive,
    disableArchive,
    selectedFsSpecObjectSlugs,
    setSelectedFsSpecObjectSlugs,
    fsSpecEnabled,
    fsSpecRoleOptions,
    areRolesDirty,
    existingSpecObjects,
  };
};
